import React, { useState, useRef, useEffect } from "react";
import { FaCircle, FaStopCircle } from "react-icons/fa";

const RecordMeeting = ({ roomId, remoteStreams = [], participants = [], identity = "" }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [chunks, setChunks] = useState([]);
    const screenStreamRef = useRef(null);
    const audioContextRef = useRef(null);

    const handleRecordingToggle = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const playAnnounceSound = () => {
        try {
            // Get participant names for the announcement
            const participantNames = participants.map(p => p.identity).filter(name => name && name !== identity);

            let announcement = "Recording has been started";

            // Use Web Speech API for text-to-speech
            const utterance = new SpeechSynthesisUtterance(announcement);
            utterance.rate = 1.0;
            utterance.pitch = 1.5; // Higher pitch for female-like voice
            utterance.volume = 2.0;

            // Function to find and set a female voice
            const setFemaleVoice = () => {
                let voices = window.speechSynthesis.getVoices();

                // If no voices loaded, try common female voice URIs directly
                let femaleVoice = null;

                if (voices.length === 0) {
                    // Create a mock voice object for common female voices
                    const commonFemaleVoices = [
                        { name: 'Samantha', voiceURI: 'com.apple.voice.compact.en-US.Samantha' },
                        { name: 'Zira', voiceURI: 'Microsoft Zira Desktop - English (United States)' },
                        { name: 'Google US English', voiceURI: 'Google US English' }
                    ];
                    femaleVoice = commonFemaleVoices[0]; // Default to Samantha on Mac
                } else {
                    // Try to find a female voice
                    femaleVoice = voices.find(voice =>
                        voice.name.toLowerCase().includes('female') ||
                        voice.name.includes('Zira') ||
                        voice.name.includes('Samantha') ||
                        voice.name.includes('Google US English') ||
                        voice.name.includes('Microsoft Zira') ||
                        voice.voiceURI.includes('female') ||
                        voice.voiceURI.includes('Samantha') ||
                        voice.voiceURI.includes('Zira')
                    );
                }

                if (femaleVoice) {
                    utterance.voice = femaleVoice;
                }

                window.speechSynthesis.speak(utterance);
            };

            // Check if voices are already loaded
            if (window.speechSynthesis.getVoices().length > 0) {
                setFemaleVoice();
            } else {
                // Wait for voices to be loaded
                window.speechSynthesis.onvoiceschanged = () => {
                    setFemaleVoice();
                };
                // Try speaking anyway after a short delay
                setTimeout(() => {
                    if (window.speechSynthesis.speaking) return;
                    setFemaleVoice();
                }, 100);
            }
        } catch (error) {
            console.error('Error playing announce sound:', error);
        }
    };

    const startRecording = async () => {
        try {
            // Get the screen share stream - this will capture whatever is being shared
            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    frameRate: { ideal: 30 }
                },
                audio: true // Try to capture system audio if available
            });

            screenStreamRef.current = screenStream;

            // Handle when user stops sharing via browser UI
            screenStream.getVideoTracks()[0].onended = () => {
                stopRecording();
            };

            // Get local audio (microphone)
            let localAudioStream = null;
            try {
                localAudioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (err) {
                console.log("Could not get microphone audio:", err);
            }

            // Create audio context for mixing audio
            audioContextRef.current = new AudioContext();
            const audioContext = audioContextRef.current;
            const destination = audioContext.createMediaStreamDestination();

            // Add local microphone audio if available
            if (localAudioStream) {
                const localSource = audioContext.createMediaStreamSource(localAudioStream);
                localSource.connect(destination);
            }

            // Add remote streams audio if available
            remoteStreams.forEach((remoteStream) => {
                if (remoteStream instanceof MediaStream) {
                    try {
                        const remoteSource = audioContext.createMediaStreamSource(remoteStream);
                        remoteSource.connect(destination);
                    } catch (err) {
                        console.log("Could not connect remote stream:", err);
                    }
                }
            });

            // Combine screen video with audio
            const combinedStream = new MediaStream([
                ...screenStream.getVideoTracks(),
                ...screenStream.getAudioTracks(),
                ...destination.stream.getAudioTracks(),
            ]);

            const recorder = new MediaRecorder(combinedStream, { mimeType: "video/webm; codecs=vp8,opus" });

            const recordedChunks = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordedChunks.push(event.data);
                }
            };

            recorder.onstop = () => {
                setChunks(recordedChunks);
                downloadRecording(recordedChunks);
                cleanup();
            };

            recorder.start(1000); // Collect data every second
            setMediaRecorder(recorder);
            setIsRecording(true);

            // Play announce sound when recording starts
            playAnnounceSound();

        } catch (error) {
            console.error("Error starting recording:", error);
            alert("Could not start recording. Please make sure to grant screen sharing permission.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }

        // Stop all tracks in the screen stream
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
        }

        setIsRecording(false);
    };

    const downloadRecording = (chunks) => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `recording-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
    };

    const cleanup = () => {
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        setChunks([]);
        setMediaRecorder(null);
    };

    useEffect(() => {
        return () => {
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
            if (screenStreamRef.current) {
                screenStreamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, []);

    return (
        <div>
            <div
                className="video_button_container tooltip"
                onClick={handleRecordingToggle}
                data-tooltip={isRecording ? "Stop Recording" : "Record Full Screen"}
            >
                <span
                    className="video_button_icon"
                    style={{
                        background: isRecording ? '#e03e3e' : 'rgba(255, 255, 255, 0.1)',
                        color: isRecording ? 'white' : '#30a030'
                    }}
                >
                    {isRecording ? (
                        <FaStopCircle size={20} />
                    ) : (
                        <FaCircle size={20} />
                    )}
                </span>
                <span className="video_button_label" style={{ color: isRecording ? '#e03e3e' : '#a0a0a0' }}>
                    {isRecording ? "Stop" : "Record"}
                </span>
            </div>
        </div>
    );
};

export default RecordMeeting;
